<template>
  <div>
    <sales-agents-invoice-commission-fee-modal
      :salesAgentInvoiceStatus="invoiceData.status"></sales-agents-invoice-commission-fee-modal>
    <div class="d-flex justify-content-between">
      <div>
        <b-btn v-if="!isNewInvoice" @click="openHistoryModal">{{$t('buttons.common.history')}}</b-btn>
        <history-modal-content></history-modal-content>
      </div>
      <div class="d-flex justify-content-between button-group">
        <b-btn v-if="isDraftInvoice && !isNewInvoice && currentRoutePermissions.change_saleagentinvoice"
               class="btn" variant="success"
               @click="approveInvoice" size="sm">
          {{$t('buttons.common.approve')}}
        </b-btn>
        <b-btn v-if="!isDraftInvoice && currentRoutePermissions.change_saleagentinvoice" class="btn"
               variant="success"
               @click="recoverInvoice" size="sm">
          {{$t('buttons.common.recover')}}
        </b-btn>
        <b-btn v-if="currentRoutePermissions.change_saleagentinvoice" class="btn" variant="secondary"
               @click="save" size="sm">
          {{$t('buttons.common.save')}}
        </b-btn>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{invoiceLabel}}
        <span v-if="!isNewInvoice"> - {{invoiceData.status}}</span>
      </h3>
      <div class="icons-wrapper">
        {{invoiceInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <b-collapse id="collapse" v-model="showCollapse">
      <div class="search-form">
        <div class="search-form__filters">
          <b-row>
            <b-col>
              <label>{{$t('labels.agents.agent_invoice_number')}}</label>
              <input class="form-control" v-model="invoiceData.invoice_number"
                     :class="{ required: !invoiceData.invoice_number && isLoadedData}"
                     :disabled="!isDraftInvoice">
            </b-col>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="invoiceData.buyer" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :class="{ required: !invoiceData.buyer && isLoadedData}"
                           :options="customers" :disabled="!isDraftInvoice"
                           select-label="" deselect-label="" selected-label=""></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.currency')}}</label>
              <multiselect v-model="invoiceData.currency" :max-height="200"
                           placeholder="" label="code" track-by="code"
                           :options="currencies" :disabled="!isDraftInvoice"
                           :class="{ required: !invoiceData.currency && isLoadedData}"
                           @input="fetchCurrencyRate"
                           select-label="" deselect-label="" selected-label=""></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.agents.vat')}}</label>
              <multiselect v-model="invoiceData.vat" :max-height="200"
                           label="name" track-by="id"
                           placeholder="" :disabled="!isDraftInvoice"
                           :options="vat" :class="{ required: !invoiceData.vat && isLoadedData}"
                           :close-on-select="true"
                           select-label="" deselect-label="" selected-label=""></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.invoices.invoice_date')}}</label>
              <datepicker v-model="invoiceData.invoice_date" placeholder=""
                          :class="{ required: !invoiceData.invoice_date && isLoadedData}"
                          lang="en" :first-day-of-week="1" :disabled="!isDraftInvoice"
                          @change="fetchCurrencyRate"></datepicker>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.additional_sum')}} ({{base_currency.symbol}})</label>
              <float-input v-model="invoiceData.additional_total" :disabled="!isDraftInvoice"
                           :class="{ required: invoiceData.additional_total === '' && isLoadedData}">
              </float-input>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.sum')}}</label>
              <float-input class="form-control" v-model="invoiceData.total" :disabled="!isDraftInvoice"
                           :class="{ required: (!invoiceData.total && invoiceData.total !== 0) && isLoadedData}"
                           :input="convertSumToBaseCurrency"
              ></float-input>
            </b-col>
            <b-col cols="3">
              <label>{{$t('labels.common.notes')}}</label>
              <b-form-textarea v-model="invoiceData.notes">
              </b-form-textarea>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="3">
              <label>{{$t('labels.common.seller')}}</label>
              <multiselect v-model="invoiceData.seller" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="agents" :disabled="!isDraftInvoice"
                           :class="{ required: !invoiceData.seller && isLoadedData}"
                           select-label="" deselect-label="" selected-label=""></multiselect>
            </b-col>
            <b-col offset="3" cols="3">
              <label>{{$t('labels.common.sum')}} ({{base_currency.symbol}})</label>
              <float-input v-model="invoiceData.total_base_curr" :disabled="true"></float-input>
              <div class="rate" v-if="invoiceData.invoice_date && invoiceData.currency && rate">
                {{$t('common_content.currency_rate_on_date',
                {course: `${base_currency.code}/${invoiceData.currency.code}`, rateDate})}} {{rate.toFixed(5)}}
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-collapse>
    <b-card no-body v-if="!isNewInvoice">
      <b-tabs card>
        <b-tab active @click="fetchInitialData">
          <template slot="title">
            <portal-target name="commission_fees_tab"></portal-target>
          </template>
          <portal to="commission_fees_tab">{{$t('tabs.agents.commission_fees')}}</portal>
          <div class="d-flex justify-content-between button-group">
            <div>
              {{$t('labels.common.selected')}}: {{selectedRowsCount}}
              <b-btn v-show="isDraftInvoice && currentRoutePermissions.change_saleagentinvoice && isLoadedData"
                     class="btn" variant="danger" @click="deleteCommissionFee" size="sm">
                {{$t('buttons.agents.delete_commission_fee')}}
              </b-btn>
            </div>
            <div>
              <save-columns-btn :grid="commissionFeesGridOptions" :grid-name="gridName"></save-columns-btn>
              <b-btn v-show="isDraftInvoice && currentRoutePermissions.change_saleagentinvoice && isLoadedData"
                     class="btn" variant="success" @click="openAddCommissionFeesModal"
                     size="sm">
                {{$t('buttons.agents.add_commission_fee')}}
              </b-btn>
            </div>
          </div>
          <ag-grid-vue class="ag-theme-balham" :gridOptions="commissionFeesGridOptions" :style="gridHeight"
                       :rowSelected="countSelectedRows" @rowDataChanged="countSelectedRows"
                       :columnDefs="salesCommissionFeesColumns" :rowData="commissionFees"
                       @rowDoubleClicked="openEditCommissionFeeModal" :postProcessPopup="postProcessPopup"
                       :pinnedBottomRowData="salesCommissionFeesPinnedRowData">
          </ag-grid-vue>
          <b-row class="d-flex justify-content-end">
            <b-col cols="3">
              <label>{{$t('labels.agents.total_commission_sum')}} ({{base_currency.code}})</label><br/>
              <label>{{$t('labels.agents.total_payment_sum')}} ({{base_currency.code}})</label><br/>
              <label>{{$t('labels.agents.difference')}} ({{base_currency.code}})</label>
            </b-col>
            <b-col cols="1">
              <label>{{invoiceData.total_commission_sum_base_curr_str}}</label><br/>
              <label>{{invoiceData.payment_sum_base_curr_wo_vat_str}}</label><br/>
              <label class="red">{{invoiceData.payment_commission_diff_base_curr_str}}</label>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab @click="openPaymentsTab">
          <template slot="title">
            <portal-target name="payments_tab"></portal-target>
          </template>
          <portal to="payments_tab">{{$t('tabs.agents.payments')}}</portal>
          <ag-grid-vue class="ag-theme-balham" :gridOptions="paymentsGridOptions" :style="gridHeight"
                       :columnDefs="paymentsColumns" :rowData="payments" :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="documents_tab"></portal-target>
          </template>
          <portal to="documents_tab">{{$t('tabs.common.documents')}}</portal>
          <app-documents :documents="documents" :isCollapse="showCollapse" :url="commonUrl + 'attachments/'"
                         @loadDocuments="loadDocuments"></app-documents>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="comments_tab"></portal-target>
          </template>
          <portal to="comments_tab">{{$t('tabs.common.comments')}}</portal>
          <app-comments :comments="comments" :url="commonUrl + 'comments/'"
                        @loadComments="loadComments"></app-comments>
        </b-tab>
      </b-tabs>
    </b-card>
    <b-modal ref="commissionFeesList" title="Add commission fee" :no-close-on-backdrop="true">
      <ag-grid-vue class="ag-theme-balham grid"
                   :gridOptions="salesAgentCommissionsGrid"
                   :columnDefs="salesAgentCommissionFeesBindingColumns"
                   :rowData="salesAgentCommissionFeesBindingList"
                   :pinnedBottomRowData="salesAgentCommissionFeesBindingPinnedRowData"
                   :postProcessPopup="postProcessPopup">
      </ag-grid-vue>
      <div slot="modal-footer" class="w-100 text-right">
        <b-btn size="sm" variant="success" @click="addCommissionFee" :disabled="!isDraftInvoice">
          {{$t('buttons.common.add')}}
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker'
  import moment from 'moment';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import SalesAgentsInvoiceCommissionFeeModal from '@/components/modals/SalesAgentsInvoiceCommissionFeeModal';
  import {
    getArrayByProperty,
    handlerResponse,
    setNotifyData
  } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { translateColumns } from '@/helpers/translate-helper';
  import Documents from '@/components/common/Documents';
  import Comments from '@/components/common/Comments';
  import FloatInput from '@/components/common/FloatInput';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      Datepicker,
      SaveColumnsBtn,
      SalesAgentsInvoiceCommissionFeeModal,
      HistoryModalContent,
      'ag-grid-vue': AgGridVue,
      'app-documents': Documents,
      'app-comments': Comments,
      FloatInput
    },
    data() {
      return {
        isLoadedData: false,
        invoiceData: {
          invoice_number: '',
          total: '',
          total_base_curr: '',
          vat: '',
          seller: '',
          currency: '',
          notes: '',
          buyer: '',
          status: 'draft',
          total_commission_sum_base_curr_str: '',
          payment_sum_base_curr_wo_vat_str: '',
          payment_commission_diff_base_curr_str: '',
          balance_str: '',
          invoice_date: null,
          additional_total: 0
        },
        rate: 0,
        rateDate: '',
        showCollapse: true,
        selectedRowsCount: 0
      }
    },
    methods: {
      approveInvoice() {
        this.$http.put(`accounting/sale_agent_invoices/${this.param}/`, {status: 'approved'})
          .then(handlerResponse).then((response) => {
          this.getInvoiceInfo(response.id);
          this.$notify(setNotifyData('Success', 'Invoice has been approved!', 'success'));
        }).catch((error) => {
          notificationsRenderer(error.body, 'error');
        });
      },
      recoverInvoice() {
        this.$http.put(`accounting/sale_agent_invoices/${this.param}/`, {status: 'draft'})
          .then(handlerResponse).then((response) => {
          this.getInvoiceInfo(response.id);
          this.$notify(setNotifyData('Success', 'Invoice has been recovered!', 'success'));
        }).catch((error) => {
          notificationsRenderer(error.body, 'error');
        });
      },
      save() {
        const saveData = {
          ...this.invoiceData,
          buyer: this.invoiceData.buyer ? this.invoiceData.buyer.id : null,
          seller: this.invoiceData.seller ? this.invoiceData.seller.id : null,
          vat: this.invoiceData.vat ? this.invoiceData.vat.id : null,
          currency: this.invoiceData.currency ? this.invoiceData.currency.id : null,
          invoice_date: this.invoiceData.invoice_date && moment(this.invoiceData.invoice_date).format(DATE_FORMAT),
          total: this.invoiceData.total,
          additional_total: this.invoiceData.additional_total,
        };
        if (this.isNewInvoice) {
          this.$http.post(`accounting/sale_agent_invoices/`, saveData)
            .then(handlerResponse).then((response) => {
            this.$notify(setNotifyData('Success', 'Invoice has been created!', 'success'));
            this.getInvoiceInfo(response.id);
            this.$router.push({path: `/sales-agent-invoices/${response.id}`});
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        } else {
          this.$http.put(`accounting/sale_agent_invoices/${this.param}/`, saveData).then(() => {
            this.getInvoiceInfo(this.param);
            this.$notify(setNotifyData('Success', 'Invoice has been saved!', 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        }
      },
      deleteCommissionFee() {
        if (this.commissionFeesGridOptions.api.getSelectedRows().length) {
          const savedData = {
            fees: getArrayByProperty(this.commissionFeesGridOptions.api.getSelectedRows(), 'id'),
            sale_agent_invoice: this.invoiceData.id,
            action: 'unbind'
          };
          this.$http.post(`accounting/sale_agent_commission_fees/bind_with_sa_invoice/`, savedData)
            .then(() => {
              this.getInvoiceInfo(this.param);
              this.$notify(setNotifyData('Success', 'Commission fees has been removed', 'success'));
            }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        } else {
          this.$notify(setNotifyData('Error', 'Select commision fee for removing', 'error'));
        }
      },
      openPaymentsTab() {
        this.$store.dispatch('salesAgentsInvoice/loadInvoicePayments', {id: this.param});
      },
      openEditCommissionFeeModal(params) {
        if (this.currentRoutePermissions.change_saleagentinvoice) {
          this.$http.get(`accounting/sale_agent_commission_fees/${params.data.id}/`).then(handlerResponse).then((response) => {
            this.$root.$emit('openSalesAgentInvoiceCommissionFeeModal', {...response, parentInvoiceID: this.param});
          });
        }
      },
      openAddCommissionFeesModal() {
        this.$store.dispatch('accounting/loadSaleAgentCommissionsBindingList', {sale_agent_invoice: this.param});
        this.$refs['commissionFeesList'].show();
      },
      addCommissionFee() {
        const savedData = {
          fees: getArrayByProperty(this.salesAgentCommissionsGrid.api.getSelectedRows(), 'id'),
          sale_agent_invoice: this.invoiceData.id,
          action: 'bind'
        };
        this.$http.post(`accounting/sale_agent_commission_fees/bind_with_sa_invoice/`, savedData).then(() => {
          this.getInvoiceInfo(this.param);
          this.$refs['commissionFeesList'].hide();
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.body, 'error');
        })
      },
      convertSumToBaseCurrency() {
        if (this.rate) {
          if (!isNaN(Number(this.invoiceData.total))) {
            this.invoiceData.total_base_curr = (this.invoiceData.total / this.rate).toFixed(2);
          } else {
            this.invoiceData.total_base_curr = '';
          }
        } else {
          if (!isNaN(Number(this.invoiceData.total))) {
            this.invoiceData.total_base_curr = this.invoiceData.total;
          } else {
            this.invoiceData.total_base_curr = '';
          }
        }
      },
      async fetchCurrencyRate() {
        if (this.invoiceData.invoice_date && this.invoiceData.currency) {
          try {
            const params = {
              date: moment(this.invoiceData.invoice_date).format(DATE_FORMAT),
              dest_curr: this.invoiceData.currency.code
            };
            const response = await this.$http.get(`woodware/list/base_currency_rate/`, {params}).then(handlerResponse);
            this.rate = response.rate;
            this.rateDate = response.date;
            this.convertSumToBaseCurrency();
          } catch (e) {
            console.log(e);
          }
        } else {
          this.rate = 0;
        }
      },
      clearData() {
        this.$store.cache.delete('salesAgentsInvoice/loadDocumentsList', {id: this.param});
        this.$store.cache.delete('salesAgentsInvoice/loadCommentsList', {id: this.param});
        if (this.comments.length) {
          this.$store.commit('salesAgentsInvoice/setCommentsList', []);
        }
        if (this.documents.length) {
          this.$store.commit('salesAgentsInvoice/setDocumentsList', []);
        }
        this.$store.commit('accounting/setSaleAgentCommissionFeesList', []);
      },
      countSelectedRows() {
        this.selectedRowsCount = getArrayByProperty(this.commissionFeesGridOptions.api.getSelectedRows(), 'id') ? getArrayByProperty(this.commissionFeesGridOptions.api.getSelectedRows(), 'id').length : 0
      },
      async getInvoiceInfo(id) {
        try {
          const response = await this.$http.get(`accounting/sale_agent_invoices/${id}/`).then(handlerResponse);
          this.invoiceData = {...response};
          await this.$store.dispatch('location/setPathName', response.invoice_number);
          this.isLoadedData = true;
          this.$store.dispatch('accounting/loadSaleAgentCommissionsFeesList', {sales_agent_invoice: this.param});
        } catch (e) {
          this.$router.push({path: `/sales-agent-invoices/new`});
          console.log(e);
          this.$notify(setNotifyData('', 'Invoice with such ID has been not found. You can create new one', 'error'));
        }
      },
      loadDocuments(cacheFlag) {
        if (cacheFlag) {
          this.$store.dispatch('salesAgentsInvoice/loadDocumentsList', {id: this.param});
        } else {
          this.$store.dispatch('salesAgentsInvoice/loadDocumentsList', {id: this.param});
        }
      },
      loadComments(cacheFlag) {
        if (cacheFlag) {
          this.$store.dispatch('salesAgentsInvoice/loadCommentsList', {id: this.param});
        } else {
          this.$store.dispatch('salesAgentsInvoice/loadCommentsList', {id: this.param});
        }
      },
      openHistoryModal() {
        this.historySettings.title = this.invoiceData.invoice_number;
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      async fetchInitialData () {
        if (!this.isNewInvoice) {
          await this.getInvoiceInfo(this.param);
          await this.fetchCurrencyRate();
          this.convertSumToBaseCurrency();
        } else {
          this.isLoadedData = true;
        }
      }
    },
    beforeMount() {
      this.$store.dispatch('generic/getCurrencies');
      this.$store.dispatch('generic/loadVat');
      this.$store.dispatch('partners/getCustomers');
      this.$store.dispatch('partners/getSaleAgents');
      translateColumns.call(this, this.paymentsColumns);
    },
    async mounted() {
      await this.fetchInitialData()
      this.$root.$on('updateSalesAgentInvoice', async (id) => {
        await this.getInvoiceInfo(id);
        await this.fetchCurrencyRate();
        this.convertSumToBaseCurrency();
      })
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    beforeDestroy() {
      this.$root.$off('updateSalesAgentInvoice');
    },
    computed: {
      ...mapState('salesAgentsInvoice', ['documents', 'comments', 'paymentsColumns', 'payments']),
      ...mapGetters('salesAgentsInvoice', {
        paymentsGridOptions: 'paymentsGridOptions'
      }),
      ...mapState('generic', ['currencies', 'vat', 'currentRoutePermissions']),
      ...mapState('accounting', ['gridName', 'salesCommissionFeesColumns',
        'salesCommissionFeesPinnedRowData', 'salesAgentCommissionFeesCount', 'salesAgentCommissionFeesBindingColumns',
        'salesAgentCommissionFeesBindingList', 'salesAgentCommissionFeesBindingPinnedRowData']),
      ...mapGetters('accounting', {
        commissionFees: 'salesAgentCommissionFeesList',
        commissionFeesGridOptions: 'commissionFeesGridOptions',
        salesAgentCommissionsGrid: 'salesAgentCommissionsGrid'
      }),
      ...mapState('partners', ['customers', 'agents']),
      ...mapState('woodware', ['base_currency']),
      param() {
        return this.$route.params.id;
      },
      isNewInvoice() {
        return this.param === 'new';
      },
      isDraftInvoice() {
        return this.invoiceData.status === 'draft';
      },
      invoiceLabel() {
        return this.isNewInvoice ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.invoice')}` : `${this.$i18n.t('common_content.invoice')} / ${this.invoiceData.invoice_number}`
      },
      invoiceInfo() {
        return this.isNewInvoice ? '' : `${this.invoiceData.created} / ${this.$i18n.t('common_content.creator')}: ${this.invoiceData.created_by}`
      },
      commonUrl() {
        return `accounting/sale_agent_invoices/${this.param}/`;
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: '400px'} : {height: '600px'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: '600px'};
        }
      },
      historySettings() {
        return {url: `sale_agent_invoice/?id=${this.param}`, title: ``};
      },
    }
  }
</script>

<style scoped>
  .rate {
    font-size: 11px;
  }

  .grid {
    height: 600px;
  }

  .red {
    color: red;
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/agents/sales-invoices/SalesAgentInvoice.vue